// Three-cards-in-a-row valuation surface. Renders the payload from
// GET /valuation/workspace/<workspace_id>/all/ (see
// backend/valuation/methods/all_methods.py::compute_all_valuations).
//
// - Recommended card is elevated + carries a "Rekommenderad" badge
// - The other two are muted but still clickable
// - Cards whose method could not be computed show "Ej tillämplig" and
//   are fully disabled
// - Selecting a non-recommended card sets it as the working method and
//   fires onMethodSelect(methodKey). A soft inline note explains that
//   the user chose an alternative
//
// Styling follows the same .qai- conventions as the sibling AI
// components (see OutreachDraftCard, DealSituationPanel, ai.css).

import { useCallback, useState } from "react";

const METHOD_ORDER = ["ebitda", "revenue", "asset"];

const METHOD_TITLES = {
  ebitda: "EBITDA-multipel",
  revenue: "Omsättningsmultipel",
  asset: "Substansvärde",
};

const CONFIDENCE_LABELS = {
  hög: "Hög",
  medel: "Medel",
  låg: "Låg",
};

function formatSekRange(low, high) {
  if (low == null || high == null) return "—";
  const scale = Math.max(Math.abs(low), Math.abs(high));
  if (scale >= 1_000_000) {
    const opts = { maximumFractionDigits: 1, minimumFractionDigits: 1 };
    const l = (low / 1_000_000).toLocaleString("sv-SE", opts);
    const h = (high / 1_000_000).toLocaleString("sv-SE", opts);
    return `${l}–${h} MSEK`;
  }
  if (scale >= 10_000) {
    const l = Math.round(low / 1_000).toLocaleString("sv-SE");
    const h = Math.round(high / 1_000).toLocaleString("sv-SE");
    return `${l}–${h} kSEK`;
  }
  return `${low.toLocaleString("sv-SE")}–${high.toLocaleString("sv-SE")} SEK`;
}

function formatBasis(result) {
  if (!result) return null;
  const label = result.basis_label || "";
  const m = result.adjusted_multiple;
  if (m != null && Number.isFinite(m)) {
    const rounded = Number(m).toLocaleString("sv-SE", {
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
    });
    return `${rounded}× ${label}`;
  }
  return label || null;
}

function confidenceLabel(conf) {
  if (!conf) return null;
  return CONFIDENCE_LABELS[conf] || conf;
}

function MethodCard({
  methodKey,
  result,
  isRecommended,
  isSelected,
  onSelect,
}) {
  const disabled = !result;
  const title = METHOD_TITLES[methodKey] || methodKey;

  const classes = ["qai-vmp-card"];
  if (isRecommended) classes.push("qai-vmp-card-recommended");
  if (isSelected && !isRecommended) classes.push("qai-vmp-card-selected");
  if (!isRecommended && !isSelected && !disabled) classes.push("qai-vmp-card-muted");
  if (disabled) classes.push("qai-vmp-card-disabled");

  const handleClick = () => {
    if (disabled) return;
    onSelect?.(methodKey);
  };

  return (
    <button
      type="button"
      className={classes.join(" ")}
      onClick={handleClick}
      disabled={disabled}
      aria-pressed={isSelected || isRecommended}
      aria-label={`${title}${disabled ? " (ej tillämplig)" : ""}`}
    >
      <div className="qai-vmp-card-head">
        <span className="qai-vmp-card-title">{title}</span>
        {isRecommended && (
          <span className="qai-vmp-badge">Rekommenderad</span>
        )}
      </div>

      {disabled ? (
        <div className="qai-vmp-card-empty">Ej tillämplig</div>
      ) : (
        <>
          <div className="qai-vmp-value">
            {formatSekRange(result.value_low, result.value_high)}
          </div>
          {formatBasis(result) && (
            <div className="qai-vmp-basis">{formatBasis(result)}</div>
          )}
          <div
            className="qai-vmp-conf"
            data-level={result.confidence || ""}
          >
            {confidenceLabel(result.confidence) || "—"} konfidens
          </div>
        </>
      )}
    </button>
  );
}

function FactorChip({ label, positive }) {
  return (
    <span
      className="qai-vmp-factor"
      data-positive={positive ? "true" : "false"}
    >
      {label}
    </span>
  );
}

export default function ValuationMethodPanel({ data, onMethodSelect }) {
  const [userSelected, setUserSelected] = useState(null);

  const handleSelect = useCallback(
    (methodKey) => {
      // Klick på redan valt kort behåller valet (idempotent).
      setUserSelected(methodKey);
      onMethodSelect?.(methodKey);
    },
    [onMethodSelect],
  );

  if (!data) return null;

  const recommended = data.recommended || null;
  const active = userSelected || recommended;
  const rationale = data.recommendation_rationale || "";
  const factors = Array.isArray(data.recommendation_factors)
    ? data.recommendation_factors
    : [];

  const showAltNote =
    userSelected && recommended && userSelected !== recommended;
  const altTitle = showAltNote
    ? METHOD_TITLES[userSelected] || userSelected
    : null;
  const recTitle = recommended
    ? METHOD_TITLES[recommended] || recommended
    : null;

  return (
    <div className="qai-vmp">
      <div className="qai-vmp-cards" role="list">
        {METHOD_ORDER.map((methodKey) => {
          const result = data[methodKey];
          return (
            <div role="listitem" key={methodKey} className="qai-vmp-card-wrap">
              <MethodCard
                methodKey={methodKey}
                result={result}
                isRecommended={active === methodKey && methodKey === recommended}
                isSelected={active === methodKey && methodKey !== recommended}
                onSelect={handleSelect}
              />
            </div>
          );
        })}
      </div>

      {showAltNote && (
        <div className="qai-vmp-alt-note" role="status">
          Du har valt <strong>{altTitle}</strong> som arbetsmetod.
          Rekommendationen var <strong>{recTitle}</strong> —
          resultatet nedan förklarar varför.
        </div>
      )}

      {(rationale || factors.length > 0) && (
        <section className="qai-vmp-rationale">
          <h4 className="qai-vmp-rationale-title">
            Varför rekommenderar vi denna metod?
          </h4>
          {rationale && (
            <p className="qai-vmp-rationale-body">{rationale}</p>
          )}
          {factors.length > 0 && (
            <div className="qai-vmp-factors">
              {factors.map((f, i) => (
                <FactorChip
                  key={`${i}-${f.label}`}
                  label={f.label}
                  positive={!!f.positive}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
